export interface BlueprintFeature {
  title: string;
  description: string;
}
export interface BlueprintTechItem {
  category: string;
  choice: string;
}
export interface BlueprintPhase {
  name: string;
  duration: string;
  focus: string;
}
export interface Blueprint {
  projectName: string;
  tagline: string;
  summary: string;
  complexity: string;
  estimatedTimeline: string;
  coreFeatures: BlueprintFeature[];
  techStack: BlueprintTechItem[];
  phases: BlueprintPhase[];
}

/** Turn a generated blueprint into a clean, shareable Markdown document. */
export function blueprintToMarkdown(bp: Blueprint, idea?: string): string {
  const lines: string[] = [];
  lines.push(`# ${bp.projectName}`);
  lines.push("");
  lines.push(`> ${bp.tagline}`);
  lines.push("");
  if (idea?.trim()) {
    lines.push(`**Original idea:** ${idea.trim()}`);
    lines.push("");
  }
  lines.push(`**Complexity:** ${bp.complexity}  `);
  lines.push(`**Estimated timeline:** ${bp.estimatedTimeline}`);
  lines.push("");
  lines.push("## Summary");
  lines.push("");
  lines.push(bp.summary);
  lines.push("");

  lines.push("## Core features");
  lines.push("");
  bp.coreFeatures?.forEach((f, i) => {
    lines.push(`${i + 1}. **${f.title}** — ${f.description}`);
  });
  lines.push("");

  lines.push("## Recommended stack");
  lines.push("");
  bp.techStack?.forEach((t) => {
    lines.push(`- **${t.category}:** ${t.choice}`);
  });
  lines.push("");

  lines.push("## Delivery phases");
  lines.push("");
  bp.phases?.forEach((p, i) => {
    lines.push(`${i + 1}. **${p.name}** _(${p.duration})_ — ${p.focus}`);
  });
  lines.push("");

  lines.push("---");
  lines.push("");
  lines.push(
    "_AI-generated estimate by the DevCore Blueprint Engine. Final scope is confirmed in a discovery call._"
  );
  lines.push("");

  return lines.join("\n");
}

/** A safe, lowercase, hyphenated file name for the blueprint markdown. */
export function blueprintFileName(bp: Blueprint): string {
  const slug =
    bp.projectName
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "project";
  return `${slug}-blueprint.md`;
}

const LAST_BLUEPRINT_KEY = "devcore_last_blueprint";

/**
 * Remember the most recently generated blueprint for the current session so any
 * contact CTA on the page can hand it off to the contact form, even when the CTA
 * lives outside the generator component. The `id` links it to its stored row so
 * a later contact submission can be recorded as a conversion.
 */
export function storeLastBlueprint(
  blueprint: Blueprint,
  idea?: string,
  id?: string,
): void {
  try {
    sessionStorage.setItem(
      LAST_BLUEPRINT_KEY,
      JSON.stringify({ blueprint, idea: idea ?? "", id: id ?? "" })
    );
  } catch {
    /* ignore */
  }
}

/** Read back the most recently generated blueprint, if any. */
export function readLastBlueprint(): { blueprint: Blueprint; idea?: string; id?: string } | null {
  try {
    const raw = sessionStorage.getItem(LAST_BLUEPRINT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { blueprint?: Blueprint; idea?: string; id?: string };
    if (parsed?.blueprint) return { blueprint: parsed.blueprint, idea: parsed.idea, id: parsed.id };
  } catch {
    /* ignore */
  }
  return null;
}

/** Forget the stored blueprint (e.g. when the user starts over). */
export function clearLastBlueprint(): void {
  try {
    sessionStorage.removeItem(LAST_BLUEPRINT_KEY);
  } catch {
    /* ignore */
  }
}
